// ============================================================================
// FarmerDocumentsPage — documents shared to the producer's farm(s) (Build S2,
// Task #18). Route: #my/documents. READ ONLY — uploads are admin-side.
// ============================================================================
// Shell + auth-guard pattern per FarmerHomePage/FarmerRegistrationsPage.
// Documents are grouped by farm when the producer belongs to more than one.
// First view of a document is acknowledged server-side when the download URL
// is minted; the badge flips from "new" after refresh.
// ============================================================================

import { Download, Eye, FileText, Loader2, LogOut, UserCircle } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { api, type FarmerDocument } from '../api';
import { useFarmerAuth } from '../farmerAuth';

export function FarmerDocumentsPage() {
  const { token, farmer, logout } = useFarmerAuth();
  const [documents, setDocuments] = useState<FarmerDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!token) return;
    setLoading(true); setError('');
    try {
      const r = await api.farmerDocuments(token);
      setDocuments(r.documents);
    } catch (err: any) {
      setError(err?.message || 'Could not load documents.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      window.location.hash = '#farmer-signin';
      return;
    }
    void refresh();
  }, [token, refresh]);

  if (!token || !farmer) return null;

  async function open(doc: FarmerDocument, disposition: 'inline' | 'attachment') {
    if (!token) return;
    setBusyId(doc.document_id); setError('');
    try {
      const r = await api.farmerDocumentDownloadUrl(token, doc.document_id, disposition);
      if (disposition === 'inline') {
        // noreferrer: never leak the signed URL as a Referer from the opened doc.
        window.open(r.download_url, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = r.download_url;
      }
      // Acknowledgment is stamped server-side when the URL is minted.
      void refresh();
    } catch (err: any) {
      setError(err?.message || 'Could not open the document.');
    } finally {
      setBusyId(null);
    }
  }

  const byFarm = new Map<string, FarmerDocument[]>();
  for (const d of documents) {
    const key = `${d.farm_code} — ${d.farm_name}`;
    const arr = byFarm.get(key) ?? [];
    arr.push(d);
    byFarm.set(key, arr);
  }
  const newCount = documents.filter((d) => !d.acknowledged_at).length;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="border-b border-gray-200 bg-white">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3" aria-label="Up in Farms — home">
            <img src="/logo.jpg" alt="Up in Farms Food Hub" className="h-12 md:h-14 w-auto" />
          </a>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-sm text-gray-600">
              <UserCircle className="inline h-4 w-4 mr-1 -mt-0.5" />
              {farmer.full_name}
            </span>
            <button
              type="button"
              onClick={() => { logout(); window.location.hash = '#'; }}
              className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-3xl mx-auto px-4 py-8 w-full space-y-5">
        <a href="#my" className="text-xs text-uif-green-dark hover:underline">← My account</a>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Documents</h1>
          <p className="text-sm text-gray-600 mt-1">
            Documents Up in Farms has shared with your farm{byFarm.size > 1 ? 's' : ''}.
          </p>
        </div>

        {newCount > 0 && (
          <div className="bg-uif-green/5 border border-uif-green/30 rounded-md px-3 py-2 text-sm text-uif-green-dark">
            {newCount} document{newCount > 1 ? 's' : ''} you haven't viewed yet — opening one records your acknowledgment.
          </div>
        )}

        {loading && (
          <div className="bg-white border border-gray-200 rounded-lg p-6 flex items-center gap-2 text-sm text-gray-600">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading your documents…
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm">{error}</div>
        )}

        {!loading && documents.length === 0 && !error && (
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
            <FileText className="h-8 w-8 mx-auto text-gray-300" />
            <p className="text-sm text-gray-500 mt-2">
              Nothing shared yet. Documents your program team shares with your farm will show up here.
            </p>
          </div>
        )}

        {[...byFarm.entries()].map(([farmLabel, docs]) => (
          <section key={farmLabel}>
            {byFarm.size > 1 && (
              <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-500 mb-2">{farmLabel}</h2>
            )}
            <div className="space-y-2">
              {docs.map((d) => (
                <article key={d.document_id} className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="font-medium text-gray-900 flex items-center gap-2 flex-wrap">
                        <FileText className="h-4 w-4 text-uif-green-dark flex-shrink-0" />
                        <span className="truncate">{d.title}</span>
                        {!d.acknowledged_at && (
                          <span className="inline-block text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full border bg-uif-green/5 border-uif-green/30 text-uif-green-dark">
                            new
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {d.type_label} · {d.file_name} · shared {new Date(d.shared_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {(d.mime_type === 'application/pdf' || d.mime_type.startsWith('image/')) && (
                        <button
                          type="button"
                          disabled={busyId === d.document_id}
                          onClick={() => void open(d, 'inline')}
                          className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                        >
                          <Eye className="h-3.5 w-3.5" /> View
                        </button>
                      )}
                      <button
                        type="button"
                        disabled={busyId === d.document_id}
                        onClick={() => void open(d, 'attachment')}
                        className="inline-flex items-center gap-1 rounded-md bg-uif-green text-white font-semibold px-3 py-1.5 text-xs hover:bg-uif-green-dark disabled:opacity-60"
                      >
                        {busyId === d.document_id
                          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          : <Download className="h-3.5 w-3.5" />} Download
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>
        ))}
      </main>

      <footer className="border-t border-gray-200 bg-white">
        <div className="max-w-3xl mx-auto px-4 py-4 text-xs text-gray-500">
          © Up in Farms Food Hub · support@upinfarms.com
        </div>
      </footer>
    </div>
  );
}
